<template>
  <div class="lib__form__main" data-cy="resume-component">
    <ValidationObserver ref="validator" slim>
      <div class="lib__form__main__content">
        <header class="lib__form__main__header">
          <h2 class="lib__h2">{{ $t('resume.smartMeter.title') }}</h2>
        </header>

        <resume-section-title :read-only="readOnly" :step="1" minimumBadgeWidth @goToStep="goToStep($event)">
          <template v-slot:icon>1</template>
          <template>{{ $t('resume.identification') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <ul class="lib__infobox-list">
              <InfoBox item level="success">
                <template v-slot:icon>
                  <div class="lib__tooltips lib__tooltips--top-left">
                    <span>
                      <i class="fas fa-map-marker-alt"></i>
                    </span>
                    <span class="lib__tooltips__data">{{ $t('identification.smartMeter.addressTooltip') }}</span>
                  </div>
                </template>
                <template>
                  <h3 class="lib__text-uppercase">{{ $t('identification.smartMeter.address') }}</h3>
                  <p>
                    {{ gridAccessPointState.consumptionAddress.houseNumber }}
                    {{ gridAccessPointState.consumptionAddress.streetName }}
                    {{ gridAccessPointState.consumptionAddress.complement || '' }}
                    {{ gridAccessPointState.consumptionAddress.complementBox || '' }} -
                    {{ gridAccessPointState.consumptionAddress.zipCode }}
                    {{ gridAccessPointState.consumptionAddress.cityName }}
                  </p>
                </template>
              </InfoBox>
              <InfoBox item level="primary">
                <template v-slot:icon>
                  <img :src="require('@ores/css-library/assets/img/icons/electricity.svg')" alt="electricity" />
                </template>
                <template>
                  <h3 class="lib__text-uppercase">{{ $t('identification.smartMeter.eanCode') }}</h3>
                  <p>{{ gridAccessPointState.ean }}</p>
                </template>
              </InfoBox>
            </ul>
          </template>
        </resume-container>

        <resume-section-title :read-only="readOnly" :step="2" minimumBadgeWidth noMarginTop
                              @goToStep="goToStep($event)">
          <template v-slot:icon>2</template>
          <template>{{ $t('resume.contact') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <resume-contact-info :address="gridAccessPointState.consumptionAddress"
                                 :contact-info="contactInfoState"></resume-contact-info>
          </template>
        </resume-container>

        <resume-section-title :read-only="readOnly" :step="3" minimumBadgeWidth noMarginTop
                              @goToStep="goToStep($event)">
          <template v-slot:icon>3</template>
          <template>{{ $t('prosumerDeclaration.installation.title') }}</template>
        </resume-section-title>
        <resume-container borderless commonMobileAndDesktopLayout>
          <template v-slot:common>
            <InfoTable>
              <template>
                <InfoTableItem>
                  <template v-slot:label>{{ $t('prosumerDeclaration.installation.productionSource') }}</template>
                  <template v-slot:value>{{ installationState.technicalInformation.productionSource.text }}</template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:label>{{ $t('prosumerDeclaration.installation.maxOutputPower') }}</template>
                  <template v-slot:value>{{ installationState.technicalInformation.maxOutputPower }}
                    {{ $t('prosumerDeclaration.installation.kva') }}
                  </template>
                </InfoTableItem>
                <InfoTableItem v-if="installationState.technicalInformation.totalPower">
                  <template v-slot:label>{{ $t('prosumerDeclaration.installation.totalPower') }}</template>
                  <template v-slot:value>{{ installationState.technicalInformation.totalPower }}
                    {{ $t('prosumerDeclaration.installation.kwc') }}
                  </template>
                </InfoTableItem>
                <InfoTableItem v-for="(inverter, index) in installationState.technicalInformation.powerInverters"
                               :key="index">
                  <template v-slot:label>{{ $t('prosumerDeclaration.installation.powerInverter') }} {{ index + 1 }}
                  </template>
                  <template v-slot:value>{{ inverter.brand }} {{ inverter.model }}</template>
                </InfoTableItem>
              </template>
            </InfoTable>
          </template>
        </resume-container>

        <resume-section-title :read-only="readOnly" :step="4" minimumBadgeWidth noMarginTop
                              @goToStep="goToStep($event)">
          <template v-slot:icon>4</template>
          <template>{{ $t('prosumerDeclaration.battery.title') }}</template>
        </resume-section-title>
        <resume-container borderless commonMobileAndDesktopLayout>
          <template v-slot:common>
            <InfoTable>
              <template>
                <InfoTableItem v-for="(battery, index) in installationState.battery.batteries"
                               v-if="installationState.battery.hasBattery === true"
                               :key="index">
                  <template v-slot:label>{{ $t('prosumerDeclaration.battery.battery') }} {{ index + 1 }}</template>
                  <template v-slot:value>{{ battery.brand }} - {{ battery.type }} - {{ battery.capacity }} W/h
                  </template>
                </InfoTableItem>
                <InfoTableItem v-if="installationState.battery.hasBattery === false">
                  <template v-slot:label>{{ $t('common.no') }}</template>
                </InfoTableItem>
              </template>
            </InfoTable>
          </template>
        </resume-container>

        <resume-section-title :read-only="readOnly" :step="5" minimumBadgeWidth noMarginTop
                              @goToStep="goToStep($event)">
          <template v-slot:icon>5</template>
          <template>{{ $t('prosumerDeclaration.relay.title') }}</template>
        </resume-section-title>
        <resume-container borderless commonMobileAndDesktopLayout>
          <template v-slot:common>
            <InfoTable>
              <template v-if="installationState.relay.hasRelay">
                <InfoTableItem v-if="installationState.relay.firstDecouplingRelay && installationState.relay.firstDecouplingRelay.brand">
                  <template v-slot:label>{{ $t('prosumerDeclaration.relay.decouplingRelay1') }}</template>
                  <template v-slot:value>{{ installationState.relay.firstDecouplingRelay.brand }} -
                    {{ installationState.relay.firstDecouplingRelay.type }}
                  </template>
                </InfoTableItem>
                <InfoTableItem v-if="installationState.relay.secondDecouplingRelay && installationState.relay.secondDecouplingRelay.brand">
                  <template v-slot:label>{{ $t('prosumerDeclaration.relay.decouplingRelay2') }}</template>
                  <template v-slot:value>{{ installationState.relay.secondDecouplingRelay.brand }} -
                    {{ installationState.relay.secondDecouplingRelay.type }}
                  </template>
                </InfoTableItem>
                <InfoTableItem v-if="installationState.relay.nonReturnRelay && installationState.relay.nonReturnRelay.brand">
                  <template v-slot:label>{{ $t('prosumerDeclaration.relay.nonReturnRelay') }}</template>
                  <template v-slot:value>{{ installationState.relay.nonReturnRelay.brand }} -
                    {{ installationState.relay.nonReturnRelay.type }} -
                    {{ installationState.relay.nonReturnRelay.power }} kVA
                  </template>
                </InfoTableItem>
              </template>
              <template v-else>
                <InfoTableItem>
                  <template v-slot:label>{{ $t('common.no') }}</template>
                </InfoTableItem>
              </template>
            </InfoTable>
          </template>
        </resume-container>

        <resume-section-title :read-only="readOnly" :step="6" minimumBadgeWidth noMarginTop
                              @goToStep="goToStep($event)">
          <template v-slot:icon>6</template>
          <template>{{ $t('prosumerDeclaration.meterReading.title') }}</template>
        </resume-section-title>
        <resume-container borderless commonMobileAndDesktopLayout>
          <template v-slot:common>
            <InfoBox>
              <template v-slot:icon>
                <i class="far fa-calendar-check"></i>
              </template>
              <template>
                <h3>{{ $t('prosumerDeclaration.meterReading.date') }}</h3>
                <p>{{ displayDate(meterReadingDateState) }}</p>
              </template>
            </InfoBox>
            <ul class="lib__infobox-list">
              <InfoBox item>
                <template v-slot:icon>
                  <img :src="require('@ores/css-library/assets/img/icons/electricity.svg')" alt="electricity" />
                </template>
                <template>
                  <h3>{{ $t('identification.smartMeter.eanCode') }}</h3>
                  <p>{{ gridAccessPointState.ean }}</p>
                </template>
              </InfoBox>

              <div v-for="(meterInfo, idx) in gridAccessPointState.meterInfos"
                   :key='"electricity_meter_" + idx'
                   class="lib__panel lib__no-margin">
                <div class="lib__panel__main">
                  <div class="lib__text-small lib__text-gray-500"><h3>{{ $t('common.meter.legend') }} :
                    {{ meterInfo.meterId }}</h3></div>

                  <ul class="lib__list lib__list--icon">
                    <li v-for="registerInfo in meterInfo.registerInfos">
                      <img v-if="registerInfo.isNight"
                           :src="require('@ores/css-library/assets/img/icons/moon.svg')"
                           alt="Night"
                           style="width: 13px; height: 13px; margin: 0 17px 0 3px" />
                      <img v-else :src="require('@ores/css-library/assets/img/icons/sun.svg')" alt="Day"
                           class="margin-right" style="width: 13px; height: 13px; margin: 0 17px 0 3px" />
                      <span style="margin-right: 5px">
                          {{ registerInfo.direction === 'production' ?
                        $t('common.meterRegister.production') :
                        $t('common.meterRegister.consumption')
                        }}
                        </span>
                      <span class="lib__text-small lib__text-gray-600" style="margin-right: 5px">
                          {{ registerInfo.isNight ?
                        $t('common.meterRegister.night') :
                        $t('common.meterRegister.day')
                        }}
                        </span>
                      <span> : <em>{{ registerInfo.value }} kWh</em></span>
                    </li>
                  </ul>
                </div>
              </div>
            </ul>
          </template>
        </resume-container>

        <resume-section-title :read-only="readOnly" :step="7" minimumBadgeWidth noMarginTop
                              @goToStep="goToStep($event)">
          <template v-slot:icon>7</template>
          <template>{{ $t('prosumerDeclaration.accounting.title') }}</template>
        </resume-section-title>
        <resume-container borderless commonMobileAndDesktopLayout>
          <template v-slot:common>
            <InfoTable>
              <template>
                <InfoTableItem>
                  <template v-slot:label>
                    {{ installationState.accounting.accountingType === 'autoConsumption' ?
                    $t('prosumerDeclaration.accounting.autoConsumption') :
                    $t('prosumerDeclaration.accounting.compensation') }}
                  </template>
                </InfoTableItem>
              </template>
            </InfoTable>
          </template>
        </resume-container>

        <resume-section-title :read-only="readOnly" :step="8" minimumBadgeWidth noMarginTop
                              @goToStep="goToStep($event)">
          <template v-slot:icon>8</template>
          <template>{{ $t('prosumerDeclaration.files.title') }}</template>
        </resume-section-title>
        <resume-container borderless commonMobileAndDesktopLayout>
          <template v-slot:common>
            <InfoTable>
              <template>
                <InfoTableItem>
                  <template v-slot:label><b>Rapport de conformité</b></template>
                </InfoTableItem>
                <InfoTableItem v-for="(file, index) in rapportFileInfoState" :key="'rapport' + index">
                  <template v-slot:label>{{ file.name }}</template>
                </InfoTableItem>
              </template>
            </InfoTable>
            <InfoTable>
              <template>
                <InfoTableItem>
                  <template v-slot:label><b>Schéma unifilaire</b></template>
                </InfoTableItem>
                <InfoTableItem v-for="(file, index) in schemaFileInfoState" :key="'schema' + index">
                  <template v-slot:label>{{ file.name }}</template>
                </InfoTableItem>
              </template>
            </InfoTable>
            <InfoTable>
              <template>
                <InfoTableItem>
                  <template v-slot:label><b>Photos</b></template>
                </InfoTableItem>
                <InfoTableItem v-for="(file, index) in pictureFileInfoState" :key="'picture' + index">
                  <template v-slot:label>{{ file.name }}</template>
                </InfoTableItem>
              </template>
            </InfoTable>
          </template>
        </resume-container>
        <p class="summary-conditions lib__text-info lib__text-center" v-html="$t('prosumerDeclaration.disclaimer')"></p>
      </div>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator'
import { State } from 'vuex-class'

import Step from '@/components/steps/Step'
import ResumeContactInfo from '@/components/display/Resume/ResumeContactInfo.vue'
import { GridAccessPoint, InstallationInformation, SendProsumerDeclarationRequest } from '@/models/prosumer-declaration'
import { ContactInfo } from '@/models'
import { dateService, FileInfo } from '@ores/vue-library'
import { AxiosRequestConfig } from 'axios'

@Component({
  name: 'summary-step',
  components: {
    ResumeContactInfo
  }
})
export default class SummaryStep extends Step {
  //#region [Property]
  @Prop({ type: Boolean, default: false }) public readonly readOnly!: boolean
  //#endregion

  //#region [Data]
  @State('requestId', { namespace: 'prosumerDeclaration' })
  public requestId!: string
  @State('lang')
  public language!: string
  @State('gridAccessPoint', { namespace: 'prosumerDeclaration/identification' })
  public gridAccessPointState!: GridAccessPoint
  @State('contactInfo', { namespace: 'prosumerDeclaration/contactInfo' })
  public contactInfoState!: ContactInfo
  @State('installation', { namespace: 'prosumerDeclaration/installation' })
  public installationState!: InstallationInformation
  @State('meterReadingDate', { namespace: 'prosumerDeclaration/meterReading' })
  public readonly meterReadingDateState!: Date | null
  @State('rapportFileInfo', { namespace: 'prosumerDeclaration/files' })
  public rapportFileInfoState!: FileInfo[]
  @State('schemaFileInfo', { namespace: 'prosumerDeclaration/files' })
  public schemaFileInfoState!: FileInfo[]
  @State('pictureFileInfo', { namespace: 'prosumerDeclaration/files' })
  public pictureFileInfoState!: FileInfo[]
  //#endregion

  //#region [Methods]
  public displayDate(date: Date): string {
    return dateService.displayDate(date)
  }

  public async save(next: boolean, valid: boolean) {
    if (next) {
      const data = this.getSendProsumerDeclarationCommand()

      const token = sessionStorage.getItem('token')

      if (!token) {
        throw new Error('No token found in sessionStorage');
      }
      
      const config: AxiosRequestConfig = { headers: { 'Authorization': token } }
      await this.$api.post('/ProsumerDeclaration', data, config)

      this.$appInsights.trackEvent('ProsumerDeclaration_StepSummary_Validated', {
        Segment: 'ProsumerDeclaration',
        WebRef: this.requestId
      })
    }
  }

  public goToStep(step: number) {
    this.$emit('goToStep', step)
  }

  private getSendProsumerDeclarationCommand(): SendProsumerDeclarationRequest {
    const data: SendProsumerDeclarationRequest = {
      webReference: this.requestId,
      language: this.language,
      gridAccessPoint: this.gridAccessPointState,
      contactInfo: this.contactInfoState,
      installation: this.installationState,
      meterReadingDate: this.meterReadingDateState ?? new Date(0)
    }

    const filteredInv = data.installation.technicalInformation.powerInverters
      .filter(inv => !!inv.brand && !!inv.model)
    data.installation.technicalInformation.powerInverters = filteredInv

    return data
  }

  //#endregion
}
</script>
